import { TFacility, TFixtureLabels } from "components/Fixtures/Fixtures.types";
import { useEffect } from "react";
import FacilityMap from "./FacilityMap";

type TFacilityNavigationProps = {
  labels: TFixtureLabels;
  facilities: TFacility[];
  activeFacilityIdx: number;
  onClick: (index: number) => void;
  renderPrice: (
    price: number,
    fractionDigitsCount: number,
    formatText: string
  ) => string;
  onPreviousClick: () => void;
  onNextClick: () => void;
  hideArrows: boolean;
};

export const headerHeight = 100;
const facilityItemNavHeight = 82;

const watchNavigationScroll = () => {
  const fixtureContent = document.getElementById("fixture-component");
  const facilityNavigation = document.getElementById("facility-navigation");
  const facilityNavigationWrapper = document.getElementById(
    "facility-navigation-wrapper"
  );
  const facilityItemNav = document.getElementById("facility-navigation-item");

  if (
    !fixtureContent ||
    !facilityNavigation ||
    !facilityNavigationWrapper ||
    !facilityItemNav
  ) {
    return;
  }

  if (window.scrollY > fixtureContent.offsetTop - headerHeight) {
    facilityNavigation?.classList.add("fixed");

    if (
      Math.abs(fixtureContent.getBoundingClientRect().top) +
        window.innerHeight >
      fixtureContent.getBoundingClientRect().height
    ) {
      facilityNavigation?.classList.remove("fixed");
      facilityNavigationWrapper.classList.add("end");
    }
  } else {
    facilityNavigation?.classList.remove("fixed");
    facilityNavigationWrapper.classList.remove("end");
  }

  const number = Math.min(
    fixtureContent.getBoundingClientRect().height - facilityItemNavHeight,
    window.scrollY - Math.abs(fixtureContent.offsetTop) + headerHeight - 1
  );

  facilityItemNav.style.top = Math.max(number, 0) + "px";
};

function FacilityNavigation({
  labels,
  onClick,
  facilities,
  activeFacilityIdx,
  renderPrice,
  onPreviousClick,
  onNextClick,
  hideArrows,
}: TFacilityNavigationProps) {
  useEffect(() => {
    if (window.innerWidth <= 1200) {
      return;
    } else {
      window.onscroll = function () {
        watchNavigationScroll();
      };
    }
  }, []);

  useEffect(() => {
    const element = document.getElementById(
      `facility-mobile-${activeFacilityIdx}`
    );

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeFacilityIdx]);

  return (
    <div
      className="o-facility-navigation__wrapper"
      id="facility-navigation-wrapper"
    >
      <div
        className="o-facility-navigation__wrapper-inner"
        id="facility-navigation"
      >
        <div className="o-facility-navigation__header">
          <div className="o-facility-navigation__header-wrap">
            <div className="o-facility-navigation__header-wrap--first-line">
              <h4>{labels.title}</h4>

              {labels.downloadUrl && (
                <a
                  className="o-facility-navigation__download-btn o-facility-navigation__download-btn--mobile"
                  href={labels.downloadUrl}
                  target="_blank"
                >
                  <span>{labels.downloadFAQs}</span>
                  <i className={`zmdi zmdi-${labels.downloadIcon}`}></i>
                </a>
              )}
            </div>
            {labels.wimbledonScheduleLink && (
              <a
                className="o-facility-navigation__schedule-btn"
                href={labels.wimbledonScheduleLink}
                target="_blank"
                rel="noreferrer"
              >
                <span>{labels.scheduleOfPlayText}</span>
                <i className="zmdi zmdi-calendar"></i>
              </a>
            )}
          </div>
        </div>
        <FacilityMap
          map={labels.dynamicMap}
          onFacilityClick={(facilityId) => {
            const facilityIndex = facilities.findIndex(
              (facilityItem) => facilityItem.id === facilityId
            );

            if (facilityIndex >= 0) {
              onClick(facilityIndex);
            }
          }}
          activeFacilityId={facilities[activeFacilityIdx].id}
        />
        <div className="o-facility-navigation">
          <div className="o-facility-navigation__inner-wrap">
            <div className="o-facility-navigation__content">
              {facilities.map((facility, index) => (
                <div
                  key={`facility-nav-${facility.id}`}
                  className={`o-facility-navigation__item ${
                    activeFacilityIdx === index
                      ? "o-facility-navigation__item--active"
                      : ""
                  }`}
                  onClick={() => onClick(index)}
                >
                  <div className="o-facility-navigation__item-title">{`${
                    index + 1
                  }. ${facility.title}`}</div>
                  <div className="o-facility-navigation__item-price">
                    {facility.isSoldOut
                      ? labels.soldOutText
                      : renderPrice(
                          facility.price,
                          0,
                          labels.facilityNavPriceFormatText
                        ) +
                        " " +
                        facility.vatText}
                  </div>
                  <div className="o-facility-navigation__item-btn">
                    <span>{labels.viewBtn}</span>
                    <i className={`zmdi zmdi-long-arrow-right`}></i>
                    <i className={`zmdi zmdi-check`}></i>
                  </div>
                </div>
              ))}
            </div>
            <div className="o-facility-navigation__btn-wrap">
              {labels.wimbledonScheduleLink && (
                <a
                  className="o-facility-navigation__schedule-btn"
                  href={labels.wimbledonScheduleLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>{labels.scheduleOfPlayText}</span>
                  <i className="zmdi zmdi-calendar"></i>
                </a>
              )}
              {labels.downloadUrl && (
                <a
                  className="o-facility-navigation__download-btn"
                  href={labels.downloadUrl}
                  target="_blank"
                >
                  <span>{labels.downloadFAQs}</span>
                  <i className={`zmdi zmdi-${labels.downloadIcon}`}></i>
                </a>
              )}
            </div>
          </div>
        </div>
        <div
          className="o-navigation-bar__wrapper"
          style={
            hideArrows
              ? {
                  display: "none",
                }
              : {}
          }
        >
          <nav
            style={
              hideArrows
                ? {
                    margin: "0 -20px",
                    padding: "0 20px",
                    background: "#FFFFFF",
                  }
                : {}
            }
          >
            <ul className="o-navigation-bar">
              {facilities.map((facility, index) => (
                <li>
                  <div
                    id={`facility-mobile-${index}`}
                    key={`facility-nav-${facility.id}`}
                    className={`o-navigation-bar__item ${
                      activeFacilityIdx === index
                        ? "o-navigation-bar__item--active"
                        : ""
                    }`}
                    onClick={() => onClick(index)}
                    style={
                      activeFacilityIdx === index
                        ? {
                            background: labels.isWimbledon
                              ? "#004125"
                              : "#353535",
                            height: "100%",
                            color: "#ffffff",
                          }
                        : {}
                    }
                  >
                    {`${index + 1}. ${facility.title}`}
                    {activeFacilityIdx === index && (
                      <svg
                        className="o-navigation-bar__active-arrow"
                        width="26"
                        height="26"
                        viewBox="0 0 26 26"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="0.5"
                          y="0.5"
                          width="25"
                          height="25"
                          rx="12.5"
                          fill="#4FE8B5"
                        />
                        <rect
                          x="0.5"
                          y="0.5"
                          width="25"
                          height="25"
                          rx="12.5"
                          stroke="#4FE8B5"
                        />
                        <path
                          d="M11.127 15.2109L17.334 9.00391L18.127 9.82422L11.127 16.8242L7.87305 13.5703L8.69336 12.75L11.127 15.2109Z"
                          fill="#242425"
                        />
                      </svg>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </nav>
          {!hideArrows && (
            <>
              <span
                onClick={onPreviousClick}
                className="o-navigation-bar__arrow o-navigation-bar__arrow--left"
              >
                <i className="zmdi zmdi-arrow-left"></i>
              </span>
              <span
                onClick={onNextClick}
                className="o-navigation-bar__arrow o-navigation-bar__arrow--right"
              >
                <i className="zmdi zmdi-arrow-right"></i>
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default FacilityNavigation;
